import { Injectable } from '@angular/core';
import { QuoteAdapter } from '@core/adapters/quote.adapter';
import { VersionedQuoteActions } from '@core/store/actions';
import {
  ProductsSelectors,
  VersionedQuoteSelectors,
} from '@core/store/selectors';
import {
  isProtectorVersionTabActive,
  getProtectorQuoteResponses,
  isProtectorVersionDeclined,
  isProtectorVersionAccepted,
  awaitingVersionChoice,
} from './versioned-quote.selector';
import { Store } from '@ngrx/store';
import {
  catchError,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { UnversionedQuoteEntity } from './versioned-quote.reducer';
import { QuoteVersionUpdateResponse } from '@core/adapters/quote-retrieve.adapter';
import { ProductType } from '@core/models/api/dsm-types';
import { UpdateQuoteResponse } from '@core/models/api/response/update-quote-response.model';
import { PersonaReport } from '@entities/persona/persona.model';
import {
  UpdateQuoteRequest,
  UpdateQuoteRequestBody,
} from '@core/models/api/request/update-quote-request.model';
import { CoverageChangeRequest } from '@core/models/api/request/coverages-request.model';
import { CoverageEntity } from '@core/store/entities/coverage/coverage.entity';
import { PersonaService } from '@core/services/persona.service';
import { EligibleDiscountsService } from '@core/services/eligible-discounts.service';
import { EligibleDiscountRequest } from '@core/models/api/request/eligible-discount-request.model';
import { getPersonaReport } from '@entities/persona/persona.selector';

@Injectable({
  providedIn: 'root',
})
export class VersionedQuoteService {
  constructor(
    private store: Store,
    private quoteAdapter: QuoteAdapter,
    private personaService: PersonaService,
    private eligibleDiscountsService: EligibleDiscountsService
  ) {}

  /* updateQuote for all in (quotes), setting `isVersioningEnabled = true`.
   * Updates versioned-quote store on successes, and eats errors.
   */
  makeAllQuotesVersionable(quotes: UnversionedQuoteEntity[]): Observable<any> {
    const unversionableQuotes = quotes.filter((q) => !q.isVersioningEnabled);
    if (unversionableQuotes.length < 1) {
      return of(null);
    }
    return forkJoin(
      unversionableQuotes.map((quote) => {
        return this.quoteAdapter
          .updateQuote({
            productType: quote.productType,
            quoteId: quote.quoteNumber,
            body: {
              productType: quote.productType,
              isVersioningEnabled: true,
            },
            suppressCallsInFlight: true,
            suppressQuoteStatusUpdate: true,
          })
          .pipe(
            tap(() => {
              this.store.dispatch(
                VersionedQuoteActions.markVersioningEnabled({
                  productType: quote.productType,
                })
              );
            }),
            catchError((e) => {
              return of(null);
            })
          );
      })
    );
  }

  /* Drop all stored versioned quotes and reload from the remote.
   * Depends on versionedQuotes.baseQuotes being set.
   */
  refreshVersions(): Observable<QuoteVersionUpdateResponse[][]> {
    this.store.dispatch(VersionedQuoteActions.removeAllVersionedQuotes());
    return this.store.select(VersionedQuoteSelectors.selectBaseQuotes).pipe(
      take(1),
      switchMap((baseQuotes) => {
        if (baseQuotes.length < 1) {
          return of([]);
        }
        return forkJoin(
          baseQuotes.map((baseQuote) => {
            return this.quoteAdapter
              .getQuoteVersions(baseQuote.productType, baseQuote.quoteNumber)
              .pipe(
                tap((response) => {
                  this.store.dispatch(
                    VersionedQuoteActions.replaceAllPerResponse({
                      response,
                      productType: baseQuote.productType,
                    })
                  );
                }),
                catchError((e) => {
                  return of([] as QuoteVersionUpdateResponse[]);
                })
              );
          })
        );
      })
    );
  }

  /* Given a DSM response of quote versions, delete any unselected ones and update the store.
   */
  deleteExcessVersions(
    byProduct: QuoteVersionUpdateResponse[][]
  ): Observable<any> {
    const products: {
      productType: ProductType | null;
      quoteId: string;
      excessVersions: QuoteVersionUpdateResponse[];
    }[] = [];
    for (const versions of byProduct) {
      const excessVersions = versions.filter((v) => !v.isSelected);
      const quoteId = versions.find((v) => v.isSelected)?.quoteNumber;
      // Delete if there are unselected versions, but not if everything is unselected -- unclear what that would mean.
      if (
        quoteId &&
        excessVersions.length > 0 &&
        excessVersions.length < versions.length
      ) {
        products.push({
          productType: null,
          quoteId,
          excessVersions,
        });
      }
    }
    if (products.length < 1) {
      return of(null);
    }
    return this.store
      .select(
        ProductsSelectors.getMultipleProductTypesByQuoteId(
          products.map((p) => p.quoteId)
        )
      )
      .pipe(
        take(1),
        switchMap((productTypes) => {
          const calls: Observable<any>[] = [];
          for (let i = productTypes.length; i-- > 0; ) {
            const productType = productTypes[i];
            if (productType) {
              const product = products[i];
              product.productType = productType;
              for (const version of product.excessVersions) {
                calls.push(
                  this.quoteAdapter
                    .deleteQuoteVersion(
                      product.productType,
                      product.quoteId,
                      version.versionNumber.toString()
                    )
                    .pipe(
                      tap(() => {
                        this.store.dispatch(
                          VersionedQuoteActions.removeVersion({
                            quoteId: version.quoteId,
                          })
                        );
                      }),
                      catchError((e) => of(null))
                    )
                );
              }
            } else {
              products.splice(i, 1);
            }
          }
          if (calls.length < 1) {
            return of(null);
          }
          return forkJoin(calls);
        })
      );
  }

  /* Any version-enabled baseQuote in the store which currently has exactly one version, add a version.
   */
  generateProtectorsVersions(): Observable<any> {
    return this.store
      .select(VersionedQuoteSelectors.getVersionedQuoteState)
      .pipe(
        take(1),
        switchMap((state) => {
          const calls: Observable<any>[] = [];
          for (const baseQuote of state.baseQuotes) {
            if (baseQuote.isVersioningEnabled) {
              const versions = state.entities.filter(
                (e) => e.quoteNumber === baseQuote.quoteNumber
              );
              if (versions.length === 1) {
                calls.push(
                  this.quoteAdapter
                    .createQuoteVersions(
                      baseQuote.productType,
                      baseQuote.quoteNumber
                    )
                    .pipe(
                      map((response) => {
                        this.store.dispatch(
                          VersionedQuoteActions.addPerResponse({
                            response,
                            productType: baseQuote.productType,
                            isProtector: true
                          })
                        );
                      }),
                      catchError((e) => of(null))
                    )
                );
              }
            }
          }
          if (calls.length < 1) {
            return of(null);
          }
          return forkJoin(calls);
        })
      );
  }

  buildUpdateQuoteRequestFromPersona(
    personaReport: PersonaReport,
    productType: ProductType,
    quoteId: string
  ): UpdateQuoteRequest {
    const quoteLevelRecommendations = personaReport.recommendations.filter(
      (rec) =>
        rec.available &&
        this.personaService.isQuoteLevelRecommendation(rec.id) &&
        this.personaService.isRecommendationApplicableToProductType(
          rec.id,
          productType
        )
    );

    const requestBody: UpdateQuoteRequestBody = {
      productType,
    };

    quoteLevelRecommendations.forEach((recommendation) => {
      switch (recommendation.id) {
        case 'Telematics':
          requestBody.hasDrivingDataConsent = true;
          requestBody.hasConnectedCarConsent = true;
          break;

        case 'Paperless':
          requestBody.docDelPreference = 'OnlineAccountAccess';
          requestBody.automateElectronicDocumentDelivery = true;
          break;

        default:
          break;
      }
    });

    return {
      productType,
      quoteId,
      body: requestBody,
      suppressCallsInFlight: true,
      suppressQuoteStatusUpdate: true,
    };
  }

  buildCoverageChangeRequestFromPersona(
    personaReport: PersonaReport,
    productType: ProductType,
    quoteId: string,
    existingCoverages: CoverageEntity[]
  ): CoverageChangeRequest | null {
    const coverageLevelRecommendations = personaReport.recommendations.filter(
      (rec) =>
        rec.available &&
        !rec.present &&
        !this.personaService.isQuoteLevelRecommendation(rec.id) &&
        !this.personaService.isDiscountLevelRecommendation(rec.id) &&
        this.personaService.isRecommendationApplicableToProductType(
          rec.id,
          productType
        )
    );

    const updatedCoverages = coverageLevelRecommendations
      ?.map((recommendation) => {
        const coverageId =
          this.personaService.getRecommendationToCoverageMapping(
            recommendation.id,
            productType
          );
        const existingCoverage = existingCoverages.find(
          (coverage) =>
            coverage.coverageId === coverageId &&
            coverage.productId === productType
        );

        if (!existingCoverage) {
          return null;
        }

        return {
          productId: existingCoverage.productId,
          coverageId: coverageId,
          coverageLevel: existingCoverage.coverageLevel,
          coverableId: existingCoverage.coverableId
            ? +existingCoverage.coverableId
            : undefined,
          selectedValue:
            this.personaService.getSelectedValueForRecommendedCoverage(
              existingCoverage
            ),
        };
      })
      .filter((coverage) => coverage !== null);

    if (!updatedCoverages.length) {
      return null;
    }

    return {
      productType,
      quoteId,
      coverages: updatedCoverages,
      suppressCallsInFlight: true,
      suppressQuoteStatusUpdate: true,
    };
  }

  buildEligibleDiscountRequestsFromPersona(
    personaReport: PersonaReport,
    productType: ProductType,
    quoteId: string
  ): EligibleDiscountRequest[] {
    const quoteLevelRecommendations = personaReport.recommendations.filter(
      (rec) =>
        rec.available &&
        this.personaService.isDiscountLevelRecommendation(rec.id) &&
        this.personaService.isRecommendationApplicableToProductType(
          rec.id,
          productType
        )
    );

    const eligibleDiscountRequests: EligibleDiscountRequest[] = [];

    quoteLevelRecommendations.forEach((recommendation) => {
      switch (recommendation.id) {
        case 'SmartHome':
          this.eligibleDiscountsService
            .getPolicyLineModifier('SmartDevice', productType)
            .pipe(take(1))
            .subscribe((discount) => {
              if (discount?.selectedOptionValue === 'Participating') {
                return;
              }
              eligibleDiscountRequests.push({
                productType,
                quoteId,
                name: 'SmartDevice',
                eligibleDiscount: {
                  ...discount,
                  selectedOptionValue: 'Participating',
                },
                suppressCallsInFlight: true,
                suppressQuoteStatusUpdate: true,
              } as EligibleDiscountRequest);
            });
          break;

        default:
          break;
      }
    });

    return eligibleDiscountRequests;
  }

  isProtectorVersionTabActive(): Observable<boolean> {
    return this.store.select(isProtectorVersionTabActive);
  }

  isProtectorVersionAccepted(): Observable<boolean> {
    return this.store.select(isProtectorVersionAccepted);
  }

  isProtectorVersionDeclined(): Observable<boolean> {
    return this.store.select(isProtectorVersionDeclined);
  }

  awaitingVersionChoice(): Observable<boolean> {
    return this.store.select(awaitingVersionChoice);
  }

  getProtectorQuoteResponses(): Observable<UpdateQuoteResponse[]> {
    return this.store.select(getProtectorQuoteResponses);
  }

  allVersionRecommendationsPresent(): Observable<boolean> {
    return this.store.select(getPersonaReport).pipe(
      map((personaReport) => {
        return (
          personaReport.recommendations.length > 0 &&
          personaReport.recommendations
            .filter(
              (r) =>
                r.id !== 'ValuablesPlus' &&
                r.id !== 'Umbrella' &&
                r.id !== 'Auto'
            )
            .every((r) => r.present)
        );
      })
    );
  }

  continueWithCoreQuote(): void {
    this.store.dispatch(VersionedQuoteActions.continueWithCoreQuote());
  }

  continueWithProtectorQuote(): void {
    this.store.dispatch(VersionedQuoteActions.continueWithProtectorQuote());
  }

  setAwaitingVersionChoice(awaitingVersionChoice: boolean): void {
    this.store.dispatch(
      VersionedQuoteActions.setAwaitingVersionChoice({ awaitingVersionChoice })
    );
  }
}
